<template>
  <section class="badge-order-stat" :class="{ embedded }">
    <div v-if="!embedded" class="badge-order-stat-header">
      <div>
        <h2 class="badge-order-stat-title">校徽订购统计</h2>
      </div>
      <el-button size="small" @click="goBackAdmin">返回管理</el-button>
    </div>
    <div class="custom-alert-tip stat-alert-tip">
      <i class="el-icon-info"></i>
      <span>学校/年级/班级为家长自定义填写，系统已按名称归并；悬停可查看原始写法变体</span>
    </div>

    <div class="toolbar-filter-bar">
      <el-form :inline="true" class="toolbar-filter-form">
        <div class="toolbar-filter-fields">
        <el-form-item label="下单日期">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            start-placeholder="开始"
            end-placeholder="结束"
            value-format="yyyy-MM-dd"
            :clearable="true"
            class="toolbar-field-w-daterange" />
        </el-form-item>
        <el-form-item label="支付状态">
          <el-select v-model="query.payStatus" clearable placeholder="全部" class="toolbar-field-w-sm">
            <el-option label="已支付" :value="1" />
            <el-option label="待支付" :value="0" />
            <el-option label="已关闭" :value="3" />
          </el-select>
        </el-form-item>
        <el-form-item label="学校关键词">
          <el-input v-model="query.schoolKeyword" clearable placeholder="原始学校名模糊搜" class="toolbar-field-w-md" />
        </el-form-item>
        <el-form-item label="排除取消">
          <el-switch v-model="query.excludeCancelled" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" size="small" icon="el-icon-search" :loading="loading" @click="loadStatistics(true)">查询</el-button>
        </el-form-item>
        </div>
        <div class="toolbar-filter-actions">
          <el-button plain class="bo-toolbar-btn" icon="el-icon-refresh-left" @click="resetQuery">重置</el-button>
          <el-button plain class="bo-toolbar-btn" icon="el-icon-download" :loading="exportLoading" @click="exportExcel">导出 Excel</el-button>
        </div>
      </el-form>
    </div>

    <div v-if="summary" class="badge-order-stat-kpi">
      <div class="kpi-item kpi-item--orders">
        <span class="kpi-label">订单笔数</span>
        <span class="kpi-value">{{ summary.OrderCount }}</span>
      </div>
      <div class="kpi-item kpi-item--badges">
        <span class="kpi-label">校徽枚数</span>
        <span class="kpi-value">{{ summary.TotalQuantity }}</span>
      </div>
      <div class="kpi-item kpi-item--students">
        <span class="kpi-label">学生人数</span>
        <span class="kpi-value">{{ summary.StudentCount }}</span>
      </div>
      <div class="kpi-item kpi-item--schools">
        <span class="kpi-label">归并学校数</span>
        <span class="kpi-value">{{ summary.SchoolGroupCount }}</span>
      </div>
      <div class="kpi-item kpi-item--income">
        <span class="kpi-label">净收金额</span>
        <span class="kpi-value"><span class="kpi-symbol">¥</span>{{ formatMoney(summary.NetPayAmount) }}</span>
      </div>
    </div>

    <div class="badge-order-stat-table-wrap">
      <el-table
        v-loading="loading"
        :data="schools"
        row-key="MergeKey"
        size="small"
        default-expand-all
        :tree-props="{ children: 'Grades' }"
        class="badge-order-stat-table">
        <template slot="empty">
          <el-empty :image-size="90" description="暂无统计数据" style="padding: 40px 0;" />
        </template>
        <el-table-column label="学校 / 年级 / 班级" min-width="220" align="center">
          <template slot-scope="s">
            <template v-if="s.row.isClassLeaf">
              <el-tooltip v-if="s.row.NameVariants && s.row.NameVariants.length > 1" placement="top">
                <div slot="content">
                  <div>原始写法变体：</div>
                  <div v-for="(v, i) in s.row.NameVariants" :key="i">{{ v }}</div>
                </div>
                <span class="name-with-tip class-row">{{ s.row.ClassName }} <i class="el-icon-info tip-icon" /></span>
              </el-tooltip>
              <span v-else class="class-row">{{ s.row.ClassName }}</span>
            </template>
            <template v-else-if="s.row.GradeName">
              <el-tooltip v-if="s.row.NameVariants && s.row.NameVariants.length > 1" placement="top">
                <div slot="content">
                  <div>原始写法变体：</div>
                  <div v-for="(v, i) in s.row.NameVariants" :key="i">{{ v }}</div>
                </div>
                <span class="name-with-tip grade-row">{{ s.row.GradeName }} <i class="el-icon-info tip-icon" /></span>
              </el-tooltip>
              <span v-else class="grade-row">{{ s.row.GradeName }}</span>
            </template>
            <template v-else>
              <el-tooltip v-if="s.row.NameVariants && s.row.NameVariants.length > 1" placement="top">
                <div slot="content">
                  <div>原始写法变体：</div>
                  <div v-for="(v, i) in s.row.NameVariants" :key="i">{{ v }}</div>
                </div>
                <span class="name-with-tip">{{ s.row.SchoolName }} <i class="el-icon-info tip-icon" /></span>
              </el-tooltip>
              <span v-else>{{ s.row.SchoolName }}</span>
            </template>
          </template>
        </el-table-column>
        <el-table-column prop="OrderCount" label="订单" min-width="72" align="center" :resizable="false" />
        <el-table-column prop="TotalQuantity" label="枚数" min-width="72" align="center" :resizable="false" />
        <el-table-column prop="StudentCount" label="学生" min-width="72" align="center" :resizable="false" />
        <el-table-column label="退款" min-width="120" align="center" :resizable="false">
          <template slot-scope="s">
            <div class="refund-cell">
              <el-tag v-if="s.row.HasRefund" type="warning" size="mini" effect="plain">含退款</el-tag>
              <span v-else class="refund-none">—</span>
              <span v-if="s.row.HasRefund" class="refund-amount">¥{{ formatMoney(s.row.RefundAmount) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="净收" min-width="96" align="center" :resizable="false">
          <template slot-scope="s">¥{{ formatMoney(s.row.NetPayAmount) }}</template>
        </el-table-column>
      </el-table>
      <el-pagination
        v-if="schoolPage.total > 0"
        layout="total, prev, pager, next"
        :current-page="schoolPage.page"
        :page-size="schoolPage.pageSize"
        :total="schoolPage.total"
        @current-change="onSchoolPageChange" />
    </div>

    <div v-if="classPage.total > 0" class="badge-order-stat-class-section">
      <p class="section-title">班级明细（导出 Excel 含「汇总」「班级统计」「订单明细」三个 Sheet）</p>
      <el-table :data="classRows" size="mini" v-loading="loading">
        <el-table-column prop="SchoolName" label="学校" min-width="140" show-overflow-tooltip align="center" />
        <el-table-column prop="GradeName" label="年级" min-width="100" show-overflow-tooltip align="center" :resizable="false" />
        <el-table-column label="班级" min-width="120" align="center">
          <template slot-scope="s">
            <el-tooltip v-if="s.row.NameVariants && s.row.NameVariants.length > 1" placement="top">
              <div slot="content">
                <div v-for="(v, i) in s.row.NameVariants" :key="i">{{ v }}</div>
              </div>
              <span>{{ s.row.ClassName }} <i class="el-icon-info tip-icon" /></span>
            </el-tooltip>
            <span v-else>{{ s.row.ClassName }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="OrderCount" label="订单" min-width="64" align="center" :resizable="false" />
        <el-table-column prop="TotalQuantity" label="枚数" min-width="64" align="center" :resizable="false" />
        <el-table-column prop="StudentCount" label="学生" min-width="64" align="center" :resizable="false" />
        <el-table-column label="退款" min-width="120" align="center" :resizable="false">
          <template slot-scope="s">
            <div class="refund-cell">
              <el-tag v-if="s.row.HasRefund" type="warning" size="mini" effect="plain">含退款</el-tag>
              <span v-else class="refund-none">—</span>
              <span v-if="s.row.HasRefund" class="refund-amount">¥{{ formatMoney(s.row.RefundAmount) }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="净收" min-width="88" align="center" :resizable="false">
          <template slot-scope="s">¥{{ formatMoney(s.row.NetPayAmount) }}</template>
        </el-table-column>
      </el-table>
      <el-pagination
        layout="total, prev, pager, next"
        :current-page="classPage.page"
        :page-size="classPage.pageSize"
        :total="classPage.total"
        @current-change="onClassPageChange" />
    </div>

  </section>
</template>

<script>
/**
 * 校徽订购统计。可独立菜单（BadgeOrder/BadgeOrderStatistics）或嵌入 BadgeOrderAdmin Tab。
 * 最后更新：2026-09-14
 */
import { badgeOrderGetOrderStatistics, badgeOrderExportOrderStatisticsExcel } from '../../api/modules/badge-order';
export default {
  name: 'BadgeOrderStatistics',
  props: {
    /** 嵌入管理页 Tab 时使用，隐藏页头与返回按钮 */
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const end = this.formatDate(new Date())
    const start = this.formatDate(this.addDays(new Date(), -30))
    return {
      loading: false,
      exportLoading: false,
      loaded: false,
      dateRange: [start, end],
      query: {
        payStatus: 1,
        schoolKeyword: '',
        excludeCancelled: true
      },
      summary: null,
      schools: [],
      classRows: [],
      schoolPage: { page: 1, pageSize: 10, total: 0 },
      classPage: { page: 1, pageSize: 10, total: 0 }
    }
  },
  mounted() {
    this.loadStatistics()
  },
  methods: {
    /** 独立菜单页返回管理端「订购统计」Tab */
    goBackAdmin() {
      this.$router.push({ path: '/BadgeOrder/BadgeOrderAdmin', query: { tab: 'statistics' } })
    },
    /** 学校树表翻页（接口分页） */
    onSchoolPageChange(page) {
      this.schoolPage.page = page
      this.loadStatistics()
    },
    /** 班级明细翻页（接口分页） */
    onClassPageChange(page) {
      this.classPage.page = page
      this.loadStatistics()
    },
    addDays(d, n) {
      const x = new Date(d)
      x.setDate(x.getDate() + n)
      return x
    },
    formatDate(d) {
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return y + '-' + m + '-' + day
    },
    formatMoney(v) {
      const n = Number(v) || 0
      return n.toFixed(2)
    },
    resetQuery() {
      const end = this.formatDate(new Date())
      const start = this.formatDate(this.addDays(new Date(), -30))
      this.dateRange = [start, end]
      this.query = { payStatus: 1, schoolKeyword: '', excludeCancelled: true }
      this.schoolPage.page = 1
      this.classPage.page = 1
      this.loadStatistics()
    },
    buildParams() {
      const params = {
        schoolKeyword: this.query.schoolKeyword || undefined,
        excludeCancelled: this.query.excludeCancelled,
        page: this.schoolPage.page,
        pageSize: this.schoolPage.pageSize,
        classPage: this.classPage.page,
        classPageSize: this.classPage.pageSize
      }
      if (this.query.payStatus !== null && this.query.payStatus !== undefined && this.query.payStatus !== '') {
        params.payStatus = this.query.payStatus
      }
      if (this.dateRange && this.dateRange.length === 2) {
        params.dateStart = this.dateRange[0]
        params.dateEnd = this.dateRange[1]
      }
      return params
    },
    /** 导出参数：不分页，导出全量 */
    buildExportParams() {
      const params = this.buildParams()
      delete params.page
      delete params.pageSize
      delete params.classPage
      delete params.classPageSize
      return params
    },
    /** 将年级下的班级挂到树表子节点（第三层用 Classes 字段） */
    normalizeSchoolTree(rawSchools) {
      const list = (rawSchools || []).map(school => {
        const grades = (school.Grades || []).map(grade => {
          const classes = (grade.Classes || []).map(cls => ({
            ...cls,
            MergeKey: school.MergeKey + '_' + grade.MergeKey + '_' + cls.MergeKey,
            GradeName: undefined,
            SchoolName: undefined,
            isClassLeaf: true
          }))
          return {
            ...grade,
            MergeKey: school.MergeKey + '_' + grade.MergeKey,
            Classes: undefined,
            Grades: classes.length ? classes : undefined
          }
        })
        return {
          ...school,
          Grades: grades.length ? grades : undefined
        }
      })
      return list
    },
    async loadStatistics(resetPage) {
      if (resetPage) {
        this.schoolPage.page = 1
        this.classPage.page = 1
      }
      this.loading = true
      try {
        const res = await badgeOrderGetOrderStatistics(this.buildParams())
        if (res.data.success && res.data.response) {
          const data = res.data.response
          this.summary = data.Summary
          this.schools = this.normalizeSchoolTree(data.Schools)
          this.classRows = data.Classes || []
          this.schoolPage.total = data.SchoolDataCount || 0
          this.classPage.total = data.ClassDataCount || 0
          if (data.Page) this.schoolPage.page = data.Page
          if (data.ClassPage) this.classPage.page = data.ClassPage
        } else {
          this.summary = null
          this.schools = []
          this.classRows = []
          this.schoolPage.total = 0
          this.classPage.total = 0
          this.$message.error(res.data.msg || '查询失败')
        }
      } finally {
        this.loading = false
        this.loaded = true
      }
    },
    formatExportTimeStamp() {
      const d = new Date()
      const p = n => (n < 10 ? '0' + n : '' + n)
      return '' + d.getFullYear() + p(d.getMonth() + 1) + p(d.getDate()) + p(d.getHours()) + p(d.getMinutes()) + p(d.getSeconds())
    },
    async parseExportBlobErrorMsg(blob) {
      try {
        const text = await new Response(blob).text()
        const j = JSON.parse(text)
        return j.msg || j.message || j.Message || ''
      } catch (e) {
        return ''
      }
    },
    /** 导出 Excel：汇总 + 班级统计 + 订单明细 */
    async exportExcel() {
      try {
        const res = await badgeOrderExportOrderStatisticsExcel(this.buildExportParams())
        if (!res.data || !(res.data instanceof Blob) || res.data.size === 0) {
          this.$message.error('导出数据为空')
          return
        }
        const contentType = res.headers && res.headers['content-type']
        if (contentType && contentType.indexOf('application/json') >= 0) {
          const msg = await this.parseExportBlobErrorMsg(res.data)
          this.$message.error(msg || '导出失败')
          return
        }
        const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', '校徽订购统计_' + this.formatExportTimeStamp() + '.xlsx')
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(url)
        this.$message.success('导出成功')
      } catch (error) {
        const blob = error.response && error.response.data
        if (blob instanceof Blob) {
          const msg = await this.parseExportBlobErrorMsg(blob)
          this.$message.error(msg || '导出失败')
          return
        }
        this.$message.error('导出失败')
      }
    }
  }
}
</script>

<style scoped>
.badge-order-stat {
  padding: 20px 24px;
  background: #f8fafc;
  min-height: 100%;
  box-sizing: border-box;
}
.badge-order-stat.embedded {
  padding: 0;
  min-height: auto;
  background: transparent;
}
.embedded-tip {
  margin: 0 0 16px;
  padding: 8px 12px;
  background: #f1f5f9;
  border-left: 4px solid #94a3b8;
  border-radius: 0 6px 6px 0;
  color: #475569;
}
.badge-order-stat-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e2e8f0;
}
.badge-order-stat-title {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
}
.badge-order-stat-sub {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  max-width: 680px;
}
/* KPI 现代微渐变发光卡片组 */
.badge-order-stat-kpi {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}
.kpi-item {
  position: relative;
  flex: 1;
  min-width: 170px;
  padding: 18px 20px;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  box-sizing: border-box;
}
.kpi-item::after {
  content: '';
  position: absolute;
  width: 90px;
  height: 90px;
  border-radius: 50%;
  bottom: -25px;
  right: -25px;
  transition: all 0.3s ease;
}
.kpi-item:hover {
  transform: translateY(-3px);
}
.kpi-item:hover::after {
  transform: scale(1.15);
}
/* 1. 订单笔数卡片 (极光蓝) */
.kpi-item--orders {
  background: linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%);
  border: 1px solid #bae6fd;
  box-shadow: 0 4px 14px rgba(186, 230, 253, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.4);
}
.kpi-item--orders .kpi-label {
  color: #0369a1;
}
.kpi-item--orders .kpi-value {
  color: #0c4a6e;
}
.kpi-item--orders::after {
  background: radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 70%);
}
.kpi-item--orders:hover {
  box-shadow: 0 8px 20px rgba(186, 230, 253, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

/* 2. 校徽枚数卡片 (晨曦橙) */
.kpi-item--badges {
  background: linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%);
  border: 1px solid #fed7aa;
  box-shadow: 0 4px 14px rgba(253, 215, 170, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.4);
}
.kpi-item--badges .kpi-label {
  color: #c2410c;
}
.kpi-item--badges .kpi-value {
  color: #7c2d12;
}
.kpi-item--badges::after {
  background: radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 70%);
}
.kpi-item--badges:hover {
  box-shadow: 0 8px 20px rgba(253, 215, 170, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

/* 3. 学生人数卡片 (薰衣草紫) */
.kpi-item--students {
  background: linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%);
  border: 1px solid #e9d5ff;
  box-shadow: 0 4px 14px rgba(233, 213, 255, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.4);
}
.kpi-item--students .kpi-label {
  color: #7e22ce;
}
.kpi-item--students .kpi-value {
  color: #581c87;
}
.kpi-item--students::after {
  background: radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 70%);
}
.kpi-item--students:hover {
  box-shadow: 0 8px 20px rgba(233, 213, 255, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

/* 4. 归并学校数卡片 (薄荷青) */
.kpi-item--schools {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border: 1px solid #d1fae5;
  box-shadow: 0 4px 14px rgba(209, 250, 229, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.4);
}
.kpi-item--schools .kpi-label {
  color: #047857;
}
.kpi-item--schools .kpi-value {
  color: #064e3b;
}
.kpi-item--schools::after {
  background: radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 70%);
}
.kpi-item--schools:hover {
  box-shadow: 0 8px 20px rgba(209, 250, 229, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

/* 5. 净收金额卡片 (翡翠绿 - 重点突出) */
.kpi-item--income {
  background: linear-gradient(135deg, #f0fdf4 0%, #bbf7d0 100%);
  border: 1px solid #bbf7d0;
  min-width: 200px;
  box-shadow: 0 6px 18px rgba(187, 247, 208, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.5);
}
.kpi-item--income .kpi-label {
  color: #15803d;
  font-weight: 600;
}
.kpi-item--income .kpi-value {
  color: #14532d;
  font-size: 26px;
}
.kpi-item--income .kpi-symbol {
  font-size: 18px;
  margin-right: 2px;
  font-weight: 600;
}
.kpi-item--income::after {
  background: radial-gradient(circle, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0) 70%);
  width: 110px;
  height: 110px;
}
.kpi-item--income:hover {
  box-shadow: 0 10px 24px rgba(187, 247, 208, 0.7), inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.kpi-label {
  display: block;
  font-size: 13px;
  margin-bottom: 6px;
  font-weight: 500;
}
.kpi-value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
}

/* 优化树表和普通表格 */
.badge-order-stat-table-wrap {
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.02);
}
.badge-order-stat-table {
  border-radius: 0;
  overflow: hidden;
  box-shadow: none;
  border: none !important;
}
.badge-order-stat-table >>> th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-weight: 600 !important;
  border-bottom: 1px solid #e2e8f0 !important;
}
.badge-order-stat-table >>> td {
  border-bottom: 1px solid #f1f5f9 !important;
}
.badge-order-stat-table >>> .el-table__row--level-0 {
  background-color: #ffffff;
}
.badge-order-stat-table >>> .el-table__row--level-1 {
  background-color: #fbfcfe;
}
.badge-order-stat-table >>> .el-table__row--level-2 {
  background-color: #fafbfc;
}

.badge-order-stat >>> .el-pagination {
  margin-top: 0 !important;
  padding: 12px 16px !important;
  display: flex !important;
  justify-content: flex-end !important;
  background: #fafcfb !important;
  border-top: 1px solid #eef2f0 !important;
}

.grade-row {
  padding-left: 8px;
  color: #475569;
  font-weight: 500;
}
.class-row {
  padding-left: 16px;
  color: #1e293b;
}
.name-with-tip {
  cursor: help;
  border-bottom: 1px dashed #cbd5e1;
}
.tip-icon {
  color: #94a3b8;
  font-size: 13px;
  margin-left: 2px;
}

.refund-cell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-wrap: wrap;
}
.refund-amount {
  color: #c2410c;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.refund-none {
  color: #cbd5e1;
}

.badge-order-stat-class-section {
  margin-top: 28px;
  background: #ffffff;
  padding: 20px 20px 0;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  overflow: hidden;
}
.badge-order-stat-class-section >>> .el-table {
  border-radius: 6px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}
.badge-order-stat-class-section >>> th {
  background-color: #f8fafc !important;
  color: #475569 !important;
  font-weight: 600 !important;
}
.badge-order-stat-class-section >>> .el-pagination {
  margin: 0 -20px !important;
}
.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin: 0 0 12px;
  display: flex;
  align-items: center;
}
.section-title::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 14px;
  background: #3b82f6;
  border-radius: 2px;
  margin-right: 8px;
}

/* 高雅黑金蓝色提示框样式 (Alert Tip) */
.custom-alert-tip {
  display: flex;
  align-items: center;
  gap: 10px;
  background: linear-gradient(135deg, #f0f7ff 0%, #e8f4ff 100%);
  border-left: 4px solid #3b82f6;
  color: #1e3a8a;
  padding: 12px 18px;
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.5;
  margin-bottom: 18px;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.04);
  font-weight: 500;
  transition: all 0.3s ease;
  border-top: 1px solid rgba(59, 130, 246, 0.05);
  border-right: 1px solid rgba(59, 130, 246, 0.05);
  border-bottom: 1px solid rgba(59, 130, 246, 0.05);
}
.custom-alert-tip i {
  font-size: 16px;
  color: #3b82f6;
  flex-shrink: 0;
}

/* 统计页工具栏：轻量描边，与校徽订购主色一致 */
.badge-order-stat .toolbar-filter-bar .toolbar-filter-actions .bo-toolbar-btn {
  color: #0f766e !important;
  background: #fff !important;
  border-color: #d5e3df !important;
  font-weight: 500;
  border-radius: 8px;
}

.badge-order-stat .toolbar-filter-bar .toolbar-filter-actions .bo-toolbar-btn:hover,
.badge-order-stat .toolbar-filter-bar .toolbar-filter-actions .bo-toolbar-btn:focus {
  color: #0f766e !important;
  background: #e6f6f3 !important;
  border-color: #14b8a6 !important;
}

/* 强力覆盖全局样式污染：彻底消除 el-button 文字按钮在 hover/focus 状态下产生难看、过时的下划线 */
.el-button--text,
.el-button--text:hover,
.el-button--text:focus,
.el-button--text:active {
  text-decoration: none !important;
  outline: none !important;
}
</style>
